import React, { useState, useEffect } from 'react';
import { FaTimes, FaSearch } from 'react-icons/fa';
import { useI18n } from '../../i18n';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../../api';
import xlsxIcon from '../../assets/xlsx.svg';
import pdfIcon from '../../assets/pdf.svg';
import statusCompleteIcon from '../../assets/status-complete.svg';
import statusHoldIcon from '../../assets/status-hold.svg';
import statusTodoIcon from '../../assets/status-todo.svg';
import statusReopenIcon from '../../assets/status-reopen.svg';
import statusProgressIcon from '../../assets/status-prog.svg';
import DatePickerInput from '../Common/DatePickerInput';
import './TaskReportModal.css';

const toDate = (str) => str ? new Date(str + 'T00:00:00') : null;
const fmtDate = (date) => date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : '';

const TaskReportModal = ({ isOpen, onClose, project }) => {
  const { t } = useI18n();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [exporting, setExporting] = useState(null); // null, 'xlsx', or 'pdf'

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedResourceId, setSelectedResourceId] = useState('');

  const teamMembers = project?.team?.members || [];

  useEffect(() => {
    if (isOpen && project?.id) {
      resetAndFetch();
    }
  }, [isOpen, project?.id, startDate, endDate, selectedResourceId]);

  const resetAndFetch = () => {
    setTasks([]);
    setPage(1);
    setHasMore(true);
    fetchReportData(1, true);
  };

  const fetchReportData = async (pageNum, isInitial = false) => {
    if (loading || (!isInitial && !hasMore)) return;

    setLoading(true);
    try {
      const params = {
        page: pageNum,
        per_page: 20, // Increased for smoother scroll
        start_date: startDate,
        end_date: endDate,
        assigned_to: selectedResourceId || undefined,
      };
      const response = await api.get(`/v1/projects/${project.id}/tasks`, {
        params,
      });
      if (response.status) {
        const newTasks = response.data.tasks;
        setTasks((prev) => (isInitial ? newTasks : [...prev, ...newTasks]));

        if (response.data.pagination) {
          const { current_page, last_page } = response.data.pagination;
          setHasMore(current_page < last_page);
          setPage(current_page);
        } else {
          setHasMore(false);
        }
      }
    } catch (error) {
      console.error('Failed to fetch report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Strictly only trigger when scrolling towards the bottom
    const isNearBottom = scrollHeight - scrollTop <= clientHeight + 150;

    if (isNearBottom && hasMore && !loading) {
      fetchReportData(page + 1);
    }
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    setSelectedResourceId('');
  };

  const handleExport = async (type) => {
    if (exporting) return;

    setExporting(type);
    try {
      // Fetch all tasks with filters applied
      const params = {
        per_page: 10000, // Get all tasks
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        assigned_to: selectedResourceId || undefined,
      };

      const response = await api.get(`/v1/projects/${project.id}/tasks`, {
        params,
      });

      if (!response.status || !response.data.tasks) {
        throw new Error('Failed to fetch tasks');
      }

      const allTasks = response.data.tasks;

      // Prepare data for export
      const getResourceName = (task) =>
        task.assignee?.name || '-';

      const exportData = allTasks.map((task) => ({
        Tasks: task.title,
        Resource: getResourceName(task),
        'Start date': formatDate(task.start_date),
        'End date': formatDate(task.end_date || task.due_date),
        'Expected days': task.expected_days || '-',
        'Days used': calculateDaysUsed(task.start_date),
        Status: getStatusLabel(task.status),
        Priority: getPriorityLabel(task.priority),
      }));

      if (type === 'pdf') {
        // Generate PDF with landscape orientation for better fit
        const doc = new jsPDF('landscape', 'mm', 'a4');

        // Add title
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(`Tasks Report - ${project.title}`, 14, 15);

        // Add date
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 22);

        // Prepare table data
        const tableHeaders = ['Tasks', 'Resource', 'Start date', 'End date', 'Expected days', 'Days used', 'Status', 'Priority'];
        const tableData = exportData.map(row => [
          row.Tasks,
          row.Resource,
          row['Start date'],
          row['End date'],
          row['Expected days'],
          row['Days used'],
          row.Status,
          row.Priority
        ]);

        // Generate table with auto-sizing
        autoTable(doc, {
          head: [tableHeaders],
          body: tableData,
          startY: 28,
          styles: {
            fontSize: 9,
            cellPadding: 3,
          },
          headStyles: {
            fillColor: [12, 80, 151],
            textColor: 255,
            fontStyle: 'bold',
            fontSize: 10,
          },
          columnStyles: {
            0: { cellWidth: 'auto' }, // Tasks - flexible
            1: { cellWidth: 'auto' }, // Resource - flexible
            2: { cellWidth: 28 },     // Start date
            3: { cellWidth: 28 },     // End date
            4: { cellWidth: 25 },     // Expected days
            5: { cellWidth: 22 },     // Days used
            6: { cellWidth: 28 },     // Status
            7: { cellWidth: 22 },     // Priority
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245],
          },
          margin: { left: 14, right: 14 },
        });

        // Download the file
        const fileName = `tasks_report_${project.title.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
        doc.save(fileName);
        return;
      }

      // Generate XLSX file
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Tasks Report');

      // Auto-size columns
      const maxWidths = {};
      exportData.forEach((row) => {
        Object.keys(row).forEach((key) => {
          const value = String(row[key]);
          maxWidths[key] = Math.max(maxWidths[key] || key.length, value.length);
        });
      });
      worksheet['!cols'] = Object.keys(maxWidths).map((key) => ({
        wch: Math.min(maxWidths[key] + 2, 50),
      }));

      // Download the file
      const fileName = `tasks_report_${project.title.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setExporting(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date
      .toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
      .replace(/ /g, ', ');
  };

  const calculateDaysUsed = (startDateStr) => {
    if (!startDateStr) return '-';
    const start = new Date(startDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    if (start > today) return 0;
    const diffTime = today - start;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      todo: statusTodoIcon,
      in_progress: statusProgressIcon,
      done: statusCompleteIcon,
      on_hold: statusHoldIcon,
      reopen: statusReopenIcon,
    };
    return iconMap[status] || statusTodoIcon;
  };

  const getStatusLabel = (status) => {
    const labels = {
      todo: 'TO DO',
      in_progress: 'IN PROGRESS',
      done: 'COMPLETED',
      on_hold: 'HOLD',
      reopen: 'REOPEN',
    };
    return labels[status] || status.toUpperCase();
  };

  const getStatusClass = (status) => {
    const classMap = {
      todo: 'status-todo',
      in_progress: 'status-in-progress',
      done: 'status-done',
      on_hold: 'status-on-hold',
      reopen: 'status-reopen',
    };
    return classMap[status] || '';
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      urgent: 'Urgent',
    };
    return labels[priority] || priority;
  };

  const getPriorityClass = (priority) => {
    const classMap = {
      low: 'priority-low',
      medium: 'priority-medium',
      high: 'priority-high',
      urgent: 'priority-urgent',
    };
    return classMap[priority] || '';
  };

  if (!isOpen) return null;

  return (
    <div className="report-modal-overlay" onClick={onClose}>
      <div
        className="report-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="report-modal-header">
          <h2>Report</h2>
          <button className="report-modal-close" onClick={onClose}>
            <FaTimes size={18} />
          </button>
        </div>

        <div className="report-modal-content">
          <div className="report-top-actions">
            <p className="report-promo-text">
              Export this project's task report as a PDF or Excel file.
            </p>
            <div className="report-export-btns">
              <div className="export-btn-wrapper">
                <button
                  className={`export-btn ${exporting ? 'export-btn-disabled' : ''}`}
                  title="Download XLSX"
                  onClick={() => handleExport('xlsx')}
                  disabled={!!exporting}
                >
                  <img src={xlsxIcon} alt="XLSX" />
                </button>
                {exporting === 'xlsx' && (
                  <span className="export-loading">Loading...</span>
                )}
              </div>
              <div className="export-btn-wrapper">
                <button
                  className={`export-btn ${exporting ? 'export-btn-disabled' : ''}`}
                  title="Download PDF"
                  onClick={() => handleExport('pdf')}
                  disabled={!!exporting}
                >
                  <img src={pdfIcon} alt="PDF" />
                </button>
                {exporting === 'pdf' && (
                  <span className="export-loading">Loading...</span>
                )}
              </div>
            </div>
          </div>

          <div className="report-filters">
            <div className="report-filter-group">
              <label>Start date</label>
              <DatePickerInput
                selected={toDate(startDate)}
                onChange={(date) => { setStartDate(fmtDate(date)); setEndDate(''); }}
                placeholder="Start date"
                popperPlacement="bottom-start"
              />
            </div>

            <div className="report-filter-group">
              <label>End date</label>
              <DatePickerInput
                selected={toDate(endDate)}
                onChange={(date) => setEndDate(fmtDate(date))}
                minDate={toDate(startDate)}
                placeholder="End date"
                popperPlacement="bottom-start"
              />
            </div>

            <button className="report-clear-btn" onClick={handleClearFilter}>
              Clear Filter
            </button>

            <div className="report-filter-group" style={{ marginLeft: 'auto' }}>
              <select
                className="report-select-resource"
                value={selectedResourceId}
                onChange={(e) => setSelectedResourceId(e.target.value)}
              >
                <option value="">Select Resources</option>
                {teamMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="report-table-wrapper" onScroll={handleScroll}>
            <table className="report-table">
              <thead>
                <tr>
                  <th>{t('tables.headers.task')}</th>
                  <th>{t('tables.headers.resource')}</th>
                  <th>{t('tables.headers.start_date')}</th>
                  <th>{t('tables.headers.end_date')}</th>
                  <th>{t('tables.headers.expected_days')}</th>
                  <th>Days used</th>
                  <th>{t('tables.headers.status')}</th>
                  <th>{t('tables.headers.priority')}</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task.id}>
                    <td>{task.title}</td>
                    <td>{task.assignee?.name || '-'}</td>
                    <td>{formatDate(task.start_date)}</td>
                    <td>{formatDate(task.end_date || task.due_date)}</td>
                    <td>{task.expected_days || '-'}</td>
                    <td>{calculateDaysUsed(task.start_date)}</td>
                    <td>
                      <div className={`status-badge ${getStatusClass(task.status)}`}>
                        <img src={getStatusIcon(task.status)} alt="" className="status-icon" />
                        {getStatusLabel(task.status)}
                      </div>
                    </td>
                    <td>
                      <div className={`priority-badge ${getPriorityClass(task.priority)}`}>
                        {getPriorityLabel(task.priority)}
                      </div>
                    </td>
                  </tr>
                ))}
                {loading && Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`skel-${i}`}>
                    <td><div className="report-skeleton-cell w-long" /></td>
                    <td><div className="report-skeleton-cell w-med" /></td>
                    <td><div className="report-skeleton-cell w-med" /></td>
                    <td><div className="report-skeleton-cell w-med" /></td>
                    <td><div className="report-skeleton-cell w-short" /></td>
                    <td><div className="report-skeleton-cell w-short" /></td>
                    <td><div className="report-skeleton-cell w-badge" /></td>
                    <td><div className="report-skeleton-cell w-badge" /></td>
                  </tr>
                ))}
                {!loading && tasks.length === 0 && (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                      No tasks found matching your criteria.
                    </td>
                  </tr>
                )}
                {!loading && !hasMore && tasks.length > 0 && (
                  <tr>
                    <td className="ui-font-14" colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#bbb' }}>
                      End of report
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskReportModal;
